import React from 'react';
import T from 'i18n-react';
import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';

import MetaTags from 'Components/MetaTags/MetaTags';
import PageComposer from 'Components/PageComposer/PageComposer';
import StaticPages from 'Components/StaticPages/StaticPages';
import texts from 'Components/texts.json';
import { PAGE_SLUGS } from 'Utilities/consts';
import { getSlug } from 'Utilities/helpers';
import { withAuth } from 'Utilities/ssr/withAuth';
import { isStaticPageSlug } from 'Utilities/staticPageSlugs';

import { ErrorPage } from './_error.page';

T.setTexts(texts);

type MetaConfig = {
	description?: string;
	noIndex?: boolean;
	noIndexFollow?: boolean;
	title?: string;
};

const metaConfig = {
	'activate-account': {
		description: `${T.translate('metaTags.pages.activateAccount.description')}`,
		title: `${T.translate('metaTags.pages.activateAccount.title')}`,
	},
	'cancellation-and-price-adjustments': {
		description: `${T.translate('metaTags.pages.cancellationAndPriceAdjustments.description')}`,
		title: `${T.translate('metaTags.pages.cancellationAndPriceAdjustments.title')}`,
	},
	'code-of-conduct': {
		description: `${T.translate('metaTags.pages.codeOfConduct.description')}`,
		title: `${T.translate('metaTags.pages.codeOfConduct.title')}`,
	},
	'contact-us': {
		description: `${T.translate('metaTags.pages.contactUs.description')}`,
		noIndexFollow: true,
		title: `${T.translate('metaTags.pages.contactUs.title')}`,
	},
	cookies: {
		description: `${T.translate('metaTags.pages.cookies.description')}`,
		title: `${T.translate('metaTags.pages.cookies.title')}`,
	},
	'payment-terms': {
		description: `${T.translate('metaTags.pages.paymentTerms.description')}`,
		title: `${T.translate('metaTags.pages.paymentTerms.title')}`,
	},
	'payments-assurance-policy': {
		description: `${T.translate('metaTags.pages.paymentsAssurancePolicy.description')}`,
		title: `${T.translate('metaTags.pages.paymentsAssurancePolicy.title')}`,
	},
	privacy: {
		description: `${T.translate('metaTags.pages.privacy.description')}`,
		title: `${T.translate('metaTags.pages.privacy.title')}`,
	},
	terms: {
		description: `${T.translate('metaTags.pages.terms.description')}`,
		title: `${T.translate('metaTags.pages.terms.title')}`,
	},
};

const getMetaPropsFromConfig = (metaProps: MetaConfig) => {
	const { description = '', noIndex = false, noIndexFollow = false, title = '' } = metaProps;
	return { description, noIndex, noIndexFollow, ogDescription: description, ogTitle: title, title };
};

export const NextjsStaticPage: NextPage<{
	fullWidth?: boolean;
	pageUrl: string;
	query?: { signUpSidebar?: boolean };
}> = (props) => {
	const { pageUrl, query: { signUpSidebar = true } = {} } = props;
	const router = useRouter();

	// The slug must come from the user-facing URL. On a direct server load of e.g.
	// `/cookies` the GSSP `pageUrl` (from resolvedUrl) is correct, but on client-side
	// navigation the footer pushes `href=/static_page` (as `/cookies`), so resolvedUrl
	// — and therefore `pageUrl` — becomes `/static_page`. `router.asPath` always
	// reflects the masked display URL (`/cookies`) on both server and client, so prefer
	// it when it yields a valid static-page slug, falling back to `pageUrl` otherwise.
	const asPathSlug = getSlug(router.asPath || '');
	const slug = isStaticPageSlug(asPathSlug) ? asPathSlug : getSlug(pageUrl);

	if (!isStaticPageSlug(slug)) {
		return <ErrorPage />;
	}

	const metaProps = getMetaPropsFromConfig(metaConfig[slug]);

	return (
		<>
			<MetaTags {...metaProps} />
			<PageComposer pageSlug={PAGE_SLUGS.STATIC_PAGE}>
				<StaticPages signUpSidebar={signUpSidebar} slug={slug} {...props} />
			</PageComposer>
		</>
	);
};

const STATIC_PAGE_SLUG_QUERY = 'staticPageSlug';

const getStaticPageSlugFromContext = (context: GetServerSidePropsContext): string => {
	const rewrittenSlug = context.query[STATIC_PAGE_SLUG_QUERY];

	if (typeof rewrittenSlug === 'string' && isStaticPageSlug(rewrittenSlug)) {
		return rewrittenSlug;
	}

	return getSlug(context.resolvedUrl || '');
};

const getStaticPageProps: GetServerSideProps = async (context) => {
	const slug = getStaticPageSlugFromContext(context);
	const query = { ...context.query };
	delete query[STATIC_PAGE_SLUG_QUERY];

	return {
		props: {
			pageUrl: isStaticPageSlug(slug) ? `/${slug}` : context.resolvedUrl || '',
			query,
		},
	};
};

const getCancellationPageProps = withAuth('authorised', getStaticPageProps, {
	unauthorisedHardLoadDestination: '/',
});

export const getServerSideProps: GetServerSideProps = async (context) => {
	const slug = getStaticPageSlugFromContext(context);

	if (slug === 'cancellation-and-price-adjustments') {
		return getCancellationPageProps(context);
	}

	context.res.setHeader('Cache-Control', 'public, max-age=3600');
	return getStaticPageProps(context);
};

export default NextjsStaticPage;
